// Admin Recommendations page — shelves CRUD + analytics + manual batch run.
// Sits alongside DrinX AI under Insights/Commerce in the sidebar.

import { useState, useMemo } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { Sparkles, Plus, RefreshCw, Trash2, Pencil, X, Search, Play } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/utils'
import PeriodFilter, { type Period } from '../components/PeriodFilter'

type Surface = 'home' | 'product_detail' | 'post_order'

interface Shelf {
  id: string
  name_en: string
  name_ar: string
  surface: Surface
  active: number
  sort_order: number
  starts_at: string | null
  ends_at: string | null
  item_count: number
  created_at: string
}

// Admin /products endpoint returns `name` (aliased from name_en) and
// `price_fils` — different shape from the customer-facing /products route
// which returns `name_en` + `price`. Don't confuse the two.
interface Product {
  id: string
  name: string
  name_ar?: string
  brand: string | null
  image_key?: string | null
  price_fils: number
  category_slug?: string
}

interface OverviewStats {
  total_clicks: number
  conversions: number
  unique_users: number
  spend_today_micros: number
}

interface LastRun {
  id: string
  summary: string
  llm_input_tokens: number | null
  llm_output_tokens: number | null
  llm_cost_micros: number | null
  response_time_ms: number | null
  created_at: string
}

type BatchCadence = 'daily' | 'weekly' | 'biweekly' | 'monthly'

// Default cadence is weekly (see backend/worker/src/lib/recommendations/cost_guard.ts).
// Each entry is the max acceptable gap in hours before flagging the cron as stalled —
// the expected interval + a small grace period so the cadence boundary doesn't false-alarm.
const STALE_THRESHOLD_HOURS: Record<BatchCadence, number> = {
  daily:    24 + 2,      //  26h
  weekly:   24 * 7 + 24, // 192h (8d)
  biweekly: 24 * 14 + 24,// 360h (15d)
  monthly:  24 * 31 + 48,// 792h (33d)
}

const CADENCE_LABELS: Record<BatchCadence, string> = {
  daily: 'daily',
  weekly: 'weekly (Mondays)',
  biweekly: 'biweekly',
  monthly: 'monthly',
}

interface SurfaceRow {
  surface: Surface
  source: string
  clicks: number
  conversions: number
}

interface ProductRow {
  product_id: string
  name_en: string
  brand: string | null
  clicks: number
  conversions: number
}

const SURFACE_LABELS: Record<Surface, string> = {
  home: 'Home rail',
  product_detail: 'Product detail',
  post_order: 'Post-order',
}

const SOURCE_LABELS: Record<string, string> = {
  sql: 'Algorithmic (SQL)',
  llm: 'Algorithmic (LLM)',
  admin_shelf: 'Admin shelf',
}

function formatCost(micros: number) {
  return `$${(micros / 1_000_000).toFixed(4)}`
}

export default function Recommendations() {
  const qc = useQueryClient()
  const [period, setPeriod] = useState<Period>('7d')
  const [editingShelf, setEditingShelf] = useState<Shelf | null>(null)
  const [creating, setCreating] = useState(false)
  const [batchResult, setBatchResult] = useState<any>(null)
  const [runningBatch, setRunningBatch] = useState(false)

  const shelvesQ = useQuery<{ shelves: Shelf[] }>({
    queryKey: ['rec-shelves'],
    queryFn: () => api.get('/recommendations/shelves').then(r => r.data),
  })

  const lastRunQ = useQuery<{ runs: LastRun[]; cadence: BatchCadence }>({
    queryKey: ['rec-last-run'],
    queryFn: () => api.get('/recommendations/last-run').then(r => r.data),
    refetchInterval: 60_000,
  })

  const overviewQ = useQuery<OverviewStats>({
    queryKey: ['rec-overview', period],
    queryFn: () => api.get(`/recommendations/analytics/overview?period=${period}`).then(r => r.data),
    refetchInterval: 60_000,
  })

  const surfaceQ = useQuery<{ rows: SurfaceRow[] }>({
    queryKey: ['rec-by-surface', period],
    queryFn: () => api.get(`/recommendations/analytics/by-surface?period=${period}`).then(r => r.data),
  })

  const topProductsQ = useQuery<{ rows: ProductRow[] }>({
    queryKey: ['rec-top-products', period],
    queryFn: () => api.get(`/recommendations/analytics/top-products?period=${period}`).then(r => r.data),
  })

  const delShelf = useMutation({
    mutationFn: (id: string) => api.delete(`/recommendations/shelves/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rec-shelves'] }),
  })

  async function runBatch() {
    setRunningBatch(true)
    setBatchResult(null)
    try {
      const r = await api.post('/recommendations/run-batch-now')
      setBatchResult(r.data)
      qc.invalidateQueries({ queryKey: ['rec-last-run'] })
    } catch (e: any) {
      setBatchResult({ error: e?.response?.data?.message ?? e.message })
    } finally {
      setRunningBatch(false)
    }
  }

  // "5 minutes ago", "yesterday", etc. — small inline helper so the
  // last-run header doesn't need an extra dep. Falls back to the full
  // timestamp for anything older than a week.
  function relativeTime(iso: string): string {
    const ms = Date.now() - new Date(iso + 'Z').getTime()
    const minutes = Math.floor(ms / 60_000)
    if (minutes < 1) return 'just now'
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    if (days < 7) return `${days}d ago`
    return formatDateTime(iso)
  }

  const lastRun = lastRunQ.data?.runs[0]
  const cadence = lastRunQ.data?.cadence ?? 'weekly'
  const lastRunStaleH = lastRun
    ? (Date.now() - new Date(lastRun.created_at + 'Z').getTime()) / 3_600_000
    : null
  // Threshold depends on configured cadence (admin-tunable via
  // store_settings.rec_engine_cadence). Default weekly → stale at 8d.
  // Daily setups stale at 26h, monthly at ~33d, etc.
  const lastRunStale = lastRunStaleH !== null && lastRunStaleH > STALE_THRESHOLD_HOURS[cadence]

  const totalClicks = overviewQ.data?.total_clicks ?? 0
  const conversions = overviewQ.data?.conversions ?? 0
  const ctrPercent = totalClicks > 0 ? ((conversions / totalClicks) * 100).toFixed(1) : '0.0'

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Sparkles size={24} className="text-[#C41E3A]" />
          <h1 className="text-2xl font-bold text-white">Recommendations</h1>
        </div>
        <div className="flex items-center gap-3">
          <PeriodFilter value={period} onChange={setPeriod} />
          <button
            onClick={runBatch}
            disabled={runningBatch}
            className="px-3 py-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-[#2e2e2e] text-white text-sm flex items-center gap-2 disabled:opacity-50"
          >
            {runningBatch
              ? <><RefreshCw size={14} className="animate-spin" /> Running…</>
              : <><Play size={14} /> Run batch now</>}
          </button>
          <button
            onClick={() => setCreating(true)}
            className="px-3 py-2 rounded-lg bg-[#C41E3A] hover:bg-[#a01830] text-white text-sm flex items-center gap-2"
          >
            <Plus size={14} /> New shelf
          </button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
        <Kpi label="Total clicks" value={`${totalClicks}`} />
        <Kpi label="Conversions" value={`${conversions}`} sublabel={`${ctrPercent}% CTR`} />
        <Kpi label="Unique users" value={`${overviewQ.data?.unique_users ?? 0}`} />
        <Kpi label="LLM spend today" value={formatCost(overviewQ.data?.spend_today_micros ?? 0)} />
      </div>

      {/* Last-run strip — surfaces the most recent rec_batch from ai_search_logs */}
      <div className={`mb-6 px-4 py-3 rounded-lg border text-xs flex items-center gap-3 ${
        lastRunStale ? 'bg-amber-500/5 border-amber-500/30' : 'bg-white/[0.02] border-[#2e2e2e]'
      }`}>
        {lastRunQ.isLoading ? (
          <span className="text-gray-500">Loading last-run info…</span>
        ) : lastRun ? (
          <>
            <span className={lastRunStale ? 'text-amber-400 font-medium' : 'text-gray-400'}>
              Last batch: <span className="text-white">{relativeTime(lastRun.created_at)}</span>
            </span>
            <span className="text-gray-600">·</span>
            <span className="text-gray-400">{lastRun.summary}</span>
            {lastRun.llm_cost_micros !== null && (
              <>
                <span className="text-gray-600">·</span>
                <span className="text-gray-500">{formatCost(lastRun.llm_cost_micros)}</span>
              </>
            )}
            {lastRun.response_time_ms !== null && (
              <>
                <span className="text-gray-600">·</span>
                <span className="text-gray-500">{(lastRun.response_time_ms / 1000).toFixed(1)}s</span>
              </>
            )}
            <span className="ml-auto text-gray-600 text-[11px]">
              Cadence: <span className="text-gray-400">{CADENCE_LABELS[cadence]}</span>
              {lastRunStale && <span className="ml-2 text-amber-400">— overdue, check ai-logs</span>}
            </span>
          </>
        ) : (
          <span className="text-gray-500">
            No batch runs recorded yet. Tap "Run batch now" or wait for the {CADENCE_LABELS[cadence]} cron.
          </span>
        )}
      </div>

      {batchResult && (
        <div className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
            Batch run result
            <button onClick={() => setBatchResult(null)} className="ml-auto text-gray-500 hover:text-white">
              <X size={14} />
            </button>
          </h3>
          {batchResult.error ? (
            <p className="text-red-400 text-xs">{batchResult.error}</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <Stat label="Users" value={`${batchResult.users_processed}`} />
              <Stat label="LLM augmented" value={`${batchResult.llm_augmented_count}`} />
              <Stat label="SQL fallback" value={`${batchResult.fallback_count}`} />
              <Stat label="Total cost" value={formatCost(batchResult.total_cost_micros)} />
              <Stat label="Input tokens" value={batchResult.total_input_tokens.toLocaleString()} />
              <Stat label="Output tokens" value={batchResult.total_output_tokens.toLocaleString()} />
              <Stat label="Cache reads" value={batchResult.total_cache_read_tokens.toLocaleString()} />
              <Stat label="Elapsed" value={`${(batchResult.elapsed_ms / 1000).toFixed(1)}s`} />
            </div>
          )}
        </div>
      )}

      {/* Shelves */}
      <div className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg mb-6">
        <div className="px-4 py-3 border-b border-[#2e2e2e] flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">Curated shelves</h3>
          <p className="text-[10px] text-gray-500">Shelves render above algorithmic picks on their assigned surface.</p>
        </div>
        {shelvesQ.isLoading && <p className="text-gray-400 text-sm px-4 py-6">Loading…</p>}
        {shelvesQ.data && shelvesQ.data.shelves.length === 0 && (
          <p className="text-gray-500 text-sm text-center py-8">No shelves yet. Tap "New shelf" to create one.</p>
        )}
        {shelvesQ.data && shelvesQ.data.shelves.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-white/[0.02]">
              <tr className="text-gray-500 text-[10px] uppercase tracking-wide">
                <th className="text-left px-4 py-2 font-semibold">Name</th>
                <th className="text-left px-2 py-2 font-semibold">Surface</th>
                <th className="text-right px-2 py-2 font-semibold">Items</th>
                <th className="text-center px-2 py-2 font-semibold">Active</th>
                <th className="text-left px-2 py-2 font-semibold">Window</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {shelvesQ.data.shelves.map(s => (
                <tr key={s.id} className="border-t border-[#2e2e2e] hover:bg-white/[0.01]">
                  <td className="px-4 py-2.5">
                    <p className="text-white">{s.name_en}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5 font-mono" dir="rtl">{s.name_ar}</p>
                  </td>
                  <td className="px-2 py-2.5 text-gray-400 text-xs">{SURFACE_LABELS[s.surface]}</td>
                  <td className="px-2 py-2.5 text-right text-gray-300 tabular-nums">{s.item_count}</td>
                  <td className="px-2 py-2.5 text-center">
                    <span className={`inline-block w-2 h-2 rounded-full ${s.active ? 'bg-emerald-400' : 'bg-gray-600'}`} />
                  </td>
                  <td className="px-2 py-2.5 text-[10px] text-gray-500">
                    {s.starts_at || s.ends_at ? (
                      <>{s.starts_at?.slice(0, 10) ?? '…'} → {s.ends_at?.slice(0, 10) ?? '…'}</>
                    ) : <span className="text-gray-600">always</span>}
                  </td>
                  <td className="px-2 py-2.5 text-right">
                    <button onClick={() => setEditingShelf(s)} className="text-gray-400 hover:text-white p-1">
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => confirm(`Delete shelf "${s.name_en}"?`) && delShelf.mutate(s.id)}
                      className="text-gray-400 hover:text-red-400 p-1"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Analytics by surface + top products */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg p-4">
          <h3 className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-3">Clicks by surface · source</h3>
          {surfaceQ.data && surfaceQ.data.rows.length === 0 && (
            <p className="text-gray-500 text-sm">No clicks yet.</p>
          )}
          <div className="space-y-2">
            {(surfaceQ.data?.rows ?? []).map((r, i) => {
              const ctr = r.clicks > 0 ? (r.conversions / r.clicks) * 100 : 0
              return (
                <div key={i} className="flex items-center justify-between border-b border-[#2e2e2e] pb-2 last:border-0">
                  <div>
                    <p className="text-white text-sm">{SURFACE_LABELS[r.surface]}</p>
                    <p className="text-[10px] text-gray-500">{SOURCE_LABELS[r.source] ?? r.source}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white tabular-nums">{r.clicks} clicks</p>
                    <p className="text-[10px] text-emerald-400">{r.conversions} → cart ({ctr.toFixed(1)}%)</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg p-4">
          <h3 className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-3">Top recommended products</h3>
          {topProductsQ.data && topProductsQ.data.rows.length === 0 && (
            <p className="text-gray-500 text-sm">No data yet.</p>
          )}
          <div className="space-y-2">
            {(topProductsQ.data?.rows ?? []).slice(0, 15).map((p, i) => (
              <div key={i} className="flex items-center justify-between border-b border-[#2e2e2e] pb-2 last:border-0">
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm truncate">{p.name_en}</p>
                  {p.brand && <p className="text-[10px] text-gray-500">{p.brand}</p>}
                </div>
                <div className="text-right ml-2">
                  <p className="text-white tabular-nums text-sm">{p.clicks}</p>
                  {p.conversions > 0 && (
                    <p className="text-[10px] text-emerald-400">{p.conversions} → cart</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {(creating || editingShelf) && (
        <ShelfEditor
          shelf={editingShelf}
          onClose={() => { setCreating(false); setEditingShelf(null) }}
          onSaved={() => {
            setCreating(false)
            setEditingShelf(null)
            qc.invalidateQueries({ queryKey: ['rec-shelves'] })
          }}
        />
      )}

      {/* Recent activity hint */}
      {formatDateTime(overviewQ.data ? new Date().toISOString() : '')}
    </div>
  )
}

function Kpi({ label, value, sublabel }: { label: string; value: string; sublabel?: string }) {
  return (
    <div className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg p-4">
      <p className="text-gray-400 text-xs mb-2">{label}</p>
      <p className="text-xl font-bold text-white tabular-nums">{value}</p>
      {sublabel && <p className="text-[10px] text-gray-500 mt-1">{sublabel}</p>}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-gray-500 text-[10px] uppercase tracking-wide">{label}</p>
      <p className="text-white tabular-nums">{value}</p>
    </div>
  )
}

// ── Shelf editor modal ──────────────────────────────────────────────────

function ShelfEditor({
  shelf, onClose, onSaved,
}: { shelf: Shelf | null; onClose: () => void; onSaved: () => void }) {
  const [nameEn, setNameEn] = useState(shelf?.name_en ?? '')
  const [nameAr, setNameAr] = useState(shelf?.name_ar ?? '')
  const [surface, setSurface] = useState<Surface>(shelf?.surface ?? 'home')
  const [active, setActive] = useState(shelf?.active ?? 1)
  const [startsAt, setStartsAt] = useState(shelf?.starts_at?.slice(0, 10) ?? '')
  const [endsAt, setEndsAt] = useState(shelf?.ends_at?.slice(0, 10) ?? '')
  const [productIds, setProductIds] = useState<string[]>([])
  const [productsLoaded, setProductsLoaded] = useState(false)
  const [search, setSearch] = useState('')
  const [saving, setSaving] = useState(false)

  const allProductsQ = useQuery<{ products: Product[] }>({
    queryKey: ['admin-products-all'],
    queryFn: () => api.get('/products?limit=1000').then(r => r.data),
  })

  // Load existing items when editing.
  useQuery({
    queryKey: ['rec-shelf-detail', shelf?.id],
    queryFn: () => api.get(`/recommendations/shelves/${shelf!.id}`).then(r => {
      const items = r.data.items as Array<{ product_id: string }>
      setProductIds(items.map(i => i.product_id))
      setProductsLoaded(true)
      return r.data
    }),
    enabled: !!shelf,
  })

  const filtered = useMemo(() => {
    const list = allProductsQ.data?.products ?? []
    const s = search.trim().toLowerCase()
    return s ? list.filter(p => (p.name + ' ' + (p.brand ?? '')).toLowerCase().includes(s)) : list
  }, [allProductsQ.data, search])

  function toggle(id: string) {
    setProductIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  async function save() {
    if (!nameEn.trim() || !nameAr.trim()) return
    setSaving(true)
    try {
      const body: any = {
        name_en: nameEn.trim(),
        name_ar: nameAr.trim(),
        surface,
        active,
        starts_at: startsAt ? `${startsAt}T00:00:00Z` : null,
        ends_at:   endsAt   ? `${endsAt}T23:59:59Z` : null,
        product_ids: productIds,
      }
      if (shelf) {
        await api.patch(`/recommendations/shelves/${shelf.id}`, body)
      } else {
        await api.post('/recommendations/shelves', body)
      }
      onSaved()
    } catch (e: any) {
      alert(e?.response?.data?.message ?? e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2e2e2e]">
          <h2 className="text-lg font-semibold text-white">{shelf ? 'Edit shelf' : 'New shelf'}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 grid grid-cols-2 gap-4 overflow-y-auto">
          <div>
            <label className="text-[10px] text-gray-400 uppercase tracking-wide">Name (EN)</label>
            <input
              value={nameEn}
              onChange={e => setNameEn(e.target.value)}
              className="w-full bg-white/[0.04] border border-[#2e2e2e] rounded px-3 py-2 text-white text-sm mt-1"
            />
          </div>
          <div>
            <label className="text-[10px] text-gray-400 uppercase tracking-wide">Name (AR)</label>
            <input
              value={nameAr}
              onChange={e => setNameAr(e.target.value)}
              dir="rtl"
              className="w-full bg-white/[0.04] border border-[#2e2e2e] rounded px-3 py-2 text-white text-sm mt-1"
            />
          </div>
          <div>
            <label className="text-[10px] text-gray-400 uppercase tracking-wide">Surface</label>
            <select
              value={surface}
              onChange={e => setSurface(e.target.value as Surface)}
              className="w-full bg-white/[0.04] border border-[#2e2e2e] rounded px-3 py-2 text-white text-sm mt-1"
            >
              <option value="home">Home rail</option>
              <option value="product_detail">Product detail</option>
              <option value="post_order">Post-order</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-gray-400 uppercase tracking-wide">Active</label>
            <select
              value={active}
              onChange={e => setActive(parseInt(e.target.value, 10))}
              className="w-full bg-white/[0.04] border border-[#2e2e2e] rounded px-3 py-2 text-white text-sm mt-1"
            >
              <option value={1}>Yes</option>
              <option value={0}>No</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-gray-400 uppercase tracking-wide">Starts (optional)</label>
            <input
              type="date"
              value={startsAt}
              onChange={e => setStartsAt(e.target.value)}
              className="w-full bg-white/[0.04] border border-[#2e2e2e] rounded px-3 py-2 text-white text-sm mt-1"
            />
          </div>
          <div>
            <label className="text-[10px] text-gray-400 uppercase tracking-wide">Ends (optional)</label>
            <input
              type="date"
              value={endsAt}
              onChange={e => setEndsAt(e.target.value)}
              className="w-full bg-white/[0.04] border border-[#2e2e2e] rounded px-3 py-2 text-white text-sm mt-1"
            />
          </div>

          <div className="col-span-2 mt-2">
            <label className="text-[10px] text-gray-400 uppercase tracking-wide">
              Products · {productIds.length} selected
            </label>
            <div className="relative mt-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search products"
                className="w-full bg-white/[0.04] border border-[#2e2e2e] rounded pl-9 pr-3 py-2 text-white text-sm"
              />
            </div>
            <div className="mt-2 max-h-72 overflow-y-auto border border-[#2e2e2e] rounded">
              {(!allProductsQ.data || (shelf && !productsLoaded)) && (
                <p className="text-gray-500 text-sm p-4">Loading…</p>
              )}
              {allProductsQ.data && (shelf ? productsLoaded : true) && filtered.map(p => {
                const checked = productIds.includes(p.id)
                return (
                  <label
                    key={p.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-white/[0.02] cursor-pointer border-b border-[#2e2e2e] last:border-0"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(p.id)}
                      className="accent-[#C41E3A]"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm truncate">{p.name}</p>
                      {p.brand && <p className="text-[10px] text-gray-500">{p.brand}</p>}
                    </div>
                    <span className="text-[10px] text-gray-400 tabular-nums">
                      JD {(p.price_fils / 1000).toFixed(3)}
                    </span>
                  </label>
                )
              })}
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-[#2e2e2e] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded text-gray-400 hover:text-white text-sm">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || !nameEn.trim() || !nameAr.trim()}
            className="px-4 py-2 rounded bg-[#C41E3A] hover:bg-[#a01830] text-white text-sm font-semibold disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
